import { type FormEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronLeft, ArrowUp, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { getWsBaseUrl } from "@/api/client";
import { useSessionStore } from "@/hooks/useSessionStore";
import { useQueryClient } from "@tanstack/react-query";
import aiLogoUrl from "@/assets/svg/ai-logo.svg?url";
import { STEP_SUGGESTION_CONFIGS, STEP_FLOW_ORDER } from "@/constants/chatSuggestions";
import { MessageBubble, ThinkingBubble, type ChatMessage } from "./chat/MessageBubble";

type ChatPanelProps = {
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
};

export const ChatPanel = ({ collapsed = false, onToggleCollapsed }: ChatPanelProps) => {
  const { t } = useTranslation();
  const { currentStep, sessionId, projectName } = useSessionStore();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // ── Typewriter effect ─────────────────────────────────────────────────────
  // Queue of full assistant texts waiting to be animated
  const [typingQueue, setTypingQueue] = useState<string[]>([]);
  // Currently animating: { full text, how many chars are shown so far }
  const [typingDisplay, setTypingDisplay] = useState<{ full: string; shown: number } | null>(null);

  // When the queue has items and nothing is currently animating, start next
  useEffect(() => {
    if (typingDisplay !== null || typingQueue.length === 0) return;
    const [next, ...rest] = typingQueue;
    setTypingDisplay({ full: next, shown: 0 });
    setTypingQueue(rest);
  }, [typingQueue, typingDisplay]);

  // Advance the animation tick by tick
  useEffect(() => {
    if (!typingDisplay) return;
    if (typingDisplay.shown >= typingDisplay.full.length) {
      // Animation complete — commit to messages list
      const completed = typingDisplay.full;
      setMessages((prev) => [...prev, { role: "assistant", content: completed }]);
      setTypingDisplay(null);
      return;
    }
    const timer = setTimeout(() => {
      setTypingDisplay((prev) =>
        prev ? { ...prev, shown: Math.min(prev.shown + 8, prev.full.length) } : null
      );
    }, 40);
    return () => clearTimeout(timer);
  }, [typingDisplay]);
  // ─────────────────────────────────────────────────────────────────────────

  // Quand on avance vers une étape suivante, on réinitialise le chat pour que la
  // carte de suggestions de la nouvelle étape réapparaisse. Au retour arrière, on
  // conserve la conversation existante.
  const prevStepRef = useRef<string | undefined>(currentStep);
  useEffect(() => {
    const prev = prevStepRef.current;
    prevStepRef.current = currentStep;
    if (!prev || !currentStep || prev === currentStep) return;
    const prevIdx = STEP_FLOW_ORDER.indexOf(prev);
    const currIdx = STEP_FLOW_ORDER.indexOf(currentStep);
    if (prevIdx !== -1 && currIdx !== -1 && currIdx > prevIdx) {
      setMessages([]);
      setTypingQueue([]);
      setTypingDisplay(null);
      setIsThinking(false);
    }
  }, [currentStep]);

  // Allow external code to focus the input via a CustomEvent
  useEffect(() => {
    const handler = () => inputRef.current?.focus();
    window.addEventListener("focus-chat-input", handler);
    return () => window.removeEventListener("focus-chat-input", handler);
  }, []);
  const [activeTab, setActiveTab] = useState(0);

  const chatEnabled = Boolean(sessionId) && currentStep !== "project_selection";

  const sendQuickAction = (label: string) => {
    if (!chatEnabled) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    setMessages((prev) => [...prev, { role: "user", content: label }]);
    setIsThinking(true);
    const currentNotes = (window as Window & { __projectNotes?: string }).__projectNotes;
    const scenarioPrompts = (window as Window & { __scenarioPrompts?: string[] }).__scenarioPrompts;
    const taggedParagraphs = (window as Window & { __taggedParagraphs?: unknown[] }).__taggedParagraphs;
    const ttsProvider = (window as Window & { __ttsProvider?: string }).__ttsProvider;
    ws.send(JSON.stringify({ text: label, project_notes: currentNotes || undefined, scenario_prompts: scenarioPrompts || undefined, tagged_paragraphs: taggedParagraphs || undefined, tts_provider: ttsProvider || undefined }));
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [messages, typingDisplay, isThinking]);

  useEffect(() => {
    if (!chatEnabled) {
      wsRef.current?.close();
      setMessages([]);
      return;
    }
    const wsUrl = `${getWsBaseUrl()}/ws/chat?session_id=${sessionId}`;
    const socket = new WebSocket(wsUrl);
    wsRef.current = socket;
    socket.onopen = () => setStatus(null);
    socket.onclose = (event) => {
      if (event.code === 4404 || event.code === 4401) {
        setStatus("Session expirée. Veuillez re-sélectionner votre projet sur la page d'accueil.");
      } else {
        setStatus("Chat déconnecté");
      }
    };
    socket.onerror = () => setStatus("Erreur de connexion");
    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data as string) as {
          type: string;
          text?: string;
          tool?: string;
          input?: Record<string, unknown>;
          result?: string;
          message?: string;
        };
        if (payload.type === "assistant_text") {
          setIsThinking(false);
          // Push to typewriter queue instead of displaying immediately
          setTypingQueue((prev) => [...prev, payload.text ?? ""]);
        } else if (payload.type === "tool_call") {
          // Appels d'outils masqués de l'UI (inutiles pour l'utilisateur final).
        } else if (payload.type === "tool_result") {
          if (payload.tool === "auto_select_audio" || payload.tool === "select_audio_manually") {
            window.dispatchEvent(new Event("audio-selection-updated"));
          }
          if (payload.tool === "select_voice") {
            try {
              const res = typeof payload.result === "string" ? JSON.parse(payload.result) : payload.result;
              if (res?.voice_id) {
                window.dispatchEvent(new CustomEvent("voice-selected", { detail: { voice_id: res.voice_id, voice_label: res.voice_label, reason: res.reason } }));
              }
            } catch { /* ignore parse errors */ }
          }
          if (payload.tool === "update_project_notes") {
            try {
              const res = typeof payload.result === "string" ? JSON.parse(payload.result) : payload.result;
              const updatedText: string | undefined = res?.project_notes;
              if (typeof updatedText === "string" && updatedText.length > 0) {
                window.dispatchEvent(new CustomEvent("project-notes-updated", {
                  detail: { text: updatedText }
                }));
              }
            } catch { /* ignore parse errors */ }
            // Fallback: invalidate project profile so React Query refetches fresh data
            if (projectName) {
              queryClient.invalidateQueries({ queryKey: ["project-profile", projectName] });
            }
          }
          if (payload.tool === "transcribe_audio" || payload.tool === "save_analysis_result") {
            window.dispatchEvent(new Event("transcription-updated"));
          }
          if (payload.tool === "update_prompt_field") {
            try {
              const res = typeof payload.result === "string" ? JSON.parse(payload.result) : payload.result;
              if (res?.status === "ok") {
                window.dispatchEvent(new CustomEvent("scenario-prompt-updated", {
                  detail: { scenario_index: res.scenario_index ?? 0, action: res.action, content: res.content }
                }));
              }
            } catch { /* ignore parse errors */ }
          }
          if (payload.tool === "update_tagged_scenario") {
            try {
              const res = typeof payload.result === "string" ? JSON.parse(payload.result) : payload.result;
              if (res?.status === "ok" && Array.isArray(res.paragraphs)) {
                window.dispatchEvent(new CustomEvent("tagged-scenario-updated", {
                  detail: { paragraphs: res.paragraphs }
                }));
              }
            } catch { /* ignore parse errors */ }
          }
          const AUDIO_EDITOR_MUTATING_TOOLS = new Set([
            "audio_editor_add_sound",
            "audio_editor_remove_sound",
            "audio_editor_move_clip",
            "audio_editor_trim_clip",
            "audio_editor_set_volumes",
            "audio_editor_clear_track",
            "audio_editor_remix",
          ]);
          if (AUDIO_EDITOR_MUTATING_TOOLS.has(payload.tool ?? "")) {
            if (sessionId) {
              queryClient.invalidateQueries({ queryKey: ["scenario-audio", sessionId] });
              queryClient.invalidateQueries({ queryKey: ["audio-selection", sessionId] });
            }
            // Signal AudioEditorView to bypass clipsLoadedRef and reload clips
            window.dispatchEvent(new Event("agent-clips-updated"));
          }
        } else if (payload.type === "error") {
          setIsThinking(false);
          setMessages((prev) => [
            ...prev,
            { role: "system", content: payload.message ?? "Erreur inconnue", subtype: "error" },
          ]);
        } else if (payload.type === "done") {
          setIsThinking(false);
        }
      } catch (err) {
        console.error("Invalid chat payload", err);
      }
    };
    return () => socket.close();
  }, [sessionId, chatEnabled]);

  const send = (evt: FormEvent) => {
    evt.preventDefault();
    if (!input.trim()) return;
    if (!chatEnabled) {
      setStatus("Le chatbot est indisponible à cette étape.");
      return;
    }
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setStatus("Chat indisponible");
      return;
    }
    const message = input.trim();
    setMessages((prev) => [...prev, { role: "user", content: message }]);
    setIsThinking(true);
    const currentNotes = (window as Window & { __projectNotes?: string }).__projectNotes;
    const scenarioPrompts = (window as Window & { __scenarioPrompts?: string[] }).__scenarioPrompts;
    const taggedParagraphs = (window as Window & { __taggedParagraphs?: unknown[] }).__taggedParagraphs;
    const ttsProvider = (window as Window & { __ttsProvider?: string }).__ttsProvider;
    ws.send(JSON.stringify({ text: message, project_notes: currentNotes || undefined, scenario_prompts: scenarioPrompts || undefined, tagged_paragraphs: taggedParagraphs || undefined, tts_provider: ttsProvider || undefined }));
    setInput("");
  };

  // Hauteur du textarea : seule source de vérité
  const resizeInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
    el.style.overflowY = el.scrollHeight > 200 ? "auto" : "hidden";
  }, []);

  // `collapsed` : le textarea est remonté au dépliage
  useLayoutEffect(resizeInput, [input, collapsed, resizeInput]);

  // Recalcul quand la largeur change (hauteur écrite par nous : on l'ignore)
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    let lastWidth = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === lastWidth) return;
      lastWidth = el.clientWidth;
      resizeInput();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsed, resizeInput]);

  const isEmpty = !messages.some((msg) => msg.role === "user" || msg.role === "assistant") && !typingDisplay;

  if (collapsed) {
    return (
      <div className="relative flex h-full flex-col bg-white">
        <div className="px-3 pt-3.5">
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="mx-auto flex h-8 w-8 items-center justify-center rounded-xl border border-[#D0D5DD] bg-[#F8FAFC] transition-colors hover:bg-[#eef2f7]"
            aria-label="Ouvrir le panneau"
          >
            <ChevronLeft className="h-4.5 w-4.5 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1" />

        <div className="pb-5">
          <img src={aiLogoUrl} alt="Agent AI" width={34} height={34} className="mx-auto rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full bg-white">
      {/* Collapse button */}
      <div className="absolute top-3.5 left-3 z-20">
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="flex items-center justify-center w-8 h-8 rounded-xl border border-[#D0D5DD] bg-white shadow-sm hover:bg-[#eef2f7] transition-colors"
          aria-label="Réduire le panneau"
        >
          <ChevronRight className="h-4.5 w-4.5 text-muted-foreground" />
        </button>
      </div>

      {/* Main content area */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Scrollable messages container */}
        <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-4">
          
          {/* Brand header at the top */}
          <div className="flex flex-col items-center text-center pt-8 pb-4">
            <img src={aiLogoUrl} alt="Agent AI" width={48} height={48} className="rounded-full w-12 h-12" />
            <p className="mt-2 text-[16px] font-bold leading-tight text-[#0F172B] [font-family:Inter]">
              Agent AI
            </p>
            <p className="mt-0.5 text-[11px] leading-normal text-[#64748B] [font-family:Inter]">
              Vous pouvez me poser n'importe quelle question
            </p>
          </div>

          {/* Suggestion cards (initial propositions) */}
          {isEmpty && (
          <div className="flex flex-col items-center gap-4">
            {(STEP_SUGGESTION_CONFIGS[currentStep ?? ""] ?? []).map((config, idx) => (
              <div
                key={idx}
                className="w-full max-w-[240px] flex flex-col gap-3 rounded-[16px] border border-[#007AFF] bg-white p-4 text-left shadow-sm"
              >
                {/* Tabs */}
                {config.tabs && (
                  <div className="flex gap-1 border-b border-[#E2E8F0] pb-2">
                    {config.tabs.map((tab, tabIdx) => (
                      <button
                        key={tabIdx}
                        type="button"
                        onClick={() => setActiveTab(tabIdx)}
                        className={cn(
                          "text-[10px] font-medium px-2.5 py-1 rounded-full transition-colors",
                          activeTab === tabIdx
                            ? "bg-[#007AFF] text-white"
                            : "text-[#45556C] hover:bg-[#F1F5F9]"
                        )}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                )}

                {/* Chips / tags */}
                {config.chips.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {config.chips.map((chip, chipIdx) => (
                      <span
                        key={chipIdx}
                        className="inline-flex h-5 items-center justify-center whitespace-nowrap rounded-full border border-[#E2E8F0] bg-white px-2 py-0.5 text-[10px] font-normal leading-none text-[#45556C]"
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                )}

                {/* Intro text */}
                <p className="text-[13px] font-medium leading-normal text-[#007AFF]">{config.introText}</p>

                {/* Quick action buttons */}
                <div className="flex flex-wrap gap-1.5">
                  {config.quickActions.map((action, actionIdx) => (
                    <button
                      key={actionIdx}
                      type="button"
                      disabled={!chatEnabled}
                      onClick={() => sendQuickAction(action.label)}
                      className="inline-flex items-center justify-center rounded-[4px] border border-[#006EE6] bg-[#D0DEFF] px-2 py-[2px] text-[11px] font-semibold text-[#006EE6] hover:bg-[#B3CBFF] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>

                {/* Apply link */}
                {config.showApplyLink && (
                  <button
                    type="button"
                    disabled={!chatEnabled}
                    onClick={() => sendQuickAction(`Appliquer les ${config.quickActions.length} suggestions ?`)}
                    className="text-left text-[11px] font-semibold text-[#007AFF] hover:underline mt-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Appliquer les {config.quickActions.length} suggestions ?
                  </button>
                )}
              </div>
            ))}
          </div>
          )}
          {(messages.length > 0 || typingDisplay || isThinking) && (
            <div className="border-t border-[#E2E8F0] my-2 pt-2" />
          )}

          {/* Message bubbles */}
          {messages.map((msg, idx) => (
            <MessageBubble key={idx} message={msg} />
          ))}

          {/* Typewriter message */}
          {typingDisplay && (
            <MessageBubble
              message={{
                role: "assistant",
                content: typingDisplay.full.slice(0, typingDisplay.shown),
              }}
            />
          )}

          {/* Thinking bubble */}
          {isThinking && !typingDisplay && <ThinkingBubble />}
        </div>
      </div>

      {/* Input area */}
      <div className="shrink-0 bg-[#F8FAFC] px-3 py-4 rounded-b-[14px] border-x-[0.8px] border-b-[0.8px] border-[#E2E8F0]">
        {status && (
          <p className="text-[10px] text-destructive mb-1.5 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {status}
          </p>
        )}
        <form
          onSubmit={send}
          className="flex items-end justify-between gap-2 rounded-[20px] border border-[#007AFF] bg-white pl-4 pr-2 py-1.5 shadow-sm"
        >
          <textarea
            ref={inputRef}
            value={input}
            rows={1}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                send(e as unknown as React.FormEvent);
              }
            }}
            placeholder={
              chatEnabled
                ? t("chat.placeholder", { defaultValue: "Message" })
                : "Message"
            }
            disabled={!chatEnabled}
            className="flex-1 resize-none overflow-hidden [overflow-wrap:anywhere] bg-transparent py-0.5 text-[14px] font-normal leading-5 text-[#45556C] placeholder:text-[#94A3B8] outline-none disabled:cursor-not-allowed"
            style={{ minHeight: 20, maxHeight: 200 }}
          />
          <button
            type="submit"
            disabled={!chatEnabled || !input.trim()}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Envoyer"
          >
            <ArrowUp className="h-4 w-4 text-[#007AFF]" />
          </button>
        </form>
      </div>
    </div>
  );
};


